import React, {useMemo} from 'react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {Box, Flex, Image, Separator, Stack, Text, useSlotRecipe} from '@chakra-ui/react3'
import Link from '../../../../../components/link'
import ProductData from './product-data'
import {productUrlBuilder} from '../../../../../utils/url'
import Certificates from './certificates'
import useProductCertificates from '../../../hooks/use-product-certificates'
import BVProductRating from '../../../../../components/bazaarvoice/rating'
import {hasBVProductRating} from '../../../../../components/bazaarvoice/utils'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {getCharacteristicsJSONData, getImageByViewType, getProductData} from './utils'
import specificationsRecipe from './specifications.recipe'
import {useCroVariantActive} from '../../../../../components/cro'
import {CRO_EXPERIMENTS} from '../../../../../constants'

const useRecommendations = (product) => {
    // extract recommended product ids from product to use as a dependency for the effect to minimize product data fetch calls
    const recommendedIds = useMemo(
        () =>
            (product?.recommendations || [])
                .filter((item) => item.recommendationType.value === 3) // recommendation type "goes with"
                .map((item) => item.recommendedItemId)
                .join(','),
        [product?.recommendations]
    )
    const {data: recommendations} = useProducts(
        {
            parameters: {
                ids: recommendedIds,
                select: '(data.(id,name,slugUrl,c_slugUrl,productId))'
            }
        },
        {
            enabled: !!recommendedIds
        }
    )

    return {recommendations: recommendations?.data}
}

const Specifications = ({product}) => {
    const intl = useIntl()
    const recipe = useSlotRecipe({recipe: specificationsRecipe})
    const styles = useMemo(
        () => recipe({hasCompatibilityText: !!product?.c_productCompatibility}),
        [recipe, product?.c_productCompatibility]
    )
    const {recommendations} = useRecommendations(product)
    const hasProductRating = useMemo(() => hasBVProductRating(product), [product])

    const isCro63Active = useCroVariantActive(
        CRO_EXPERIMENTS.cro63PdpHideRating.experimentId,
        CRO_EXPERIMENTS.cro63PdpHideRating.variantId
    )

    const certificates = useProductCertificates(product).slice(0, 4)

    if (!product) {
        return null
    }

    // Used for concatinating both arrays of certificates
    product.c_localCertificates = product?.c_localCertificates || []
    product.c_globalCertificates = product?.c_globalCertificates || []

    // Get images
    const characteristicsImage = getImageByViewType(product, 'Dimensions')

    // Get data from Product or Master
    const dimensionsData = getProductData(product, {
        width: 'c_assembled-width',
        widthInch: 'c_assembled-width-inch',
        height: 'c_assembled-height',
        heightInch: 'c_assembled-height-inch',
        length: 'c_assembled-length',
        lengthInch: 'c_assembled-length-inch'
    })
    const netWeightData = getProductData(product, {kg: 'c_net-weight', lbs: 'c_net-weight-lbs'})
    const suitsAgeData = getProductData(
        product,
        {
            from: 'c_suitableFromAgeinMonths',
            to: 'c_suitableToAgeInMonths'
        },
        true
    )
    const suitsWeightData = getProductData(product, {
        kg: 'c_suitableForWeightUpToKg',
        lbs: 'c_suibtableForWeightUpToLbs'
    })
    const extendedWarrantyData = getProductData(product, {text: 'c_extendedProductWarranty'})
    const designersData = getProductData(product, {array: 'c_designers'})
    const characteristicsJSON = getCharacteristicsJSONData(product)

    return (
        <Flex css={styles.module}>
            <Stack css={styles.productData} separator={<Separator css={styles.divider} />}>
                <Stack css={styles.productScore}>
                    {hasProductRating && !isCro63Active && (
                        <BVProductRating product={product} variant="pdpSpec" />
                    )}
                    {!!certificates && certificates.length > 0 && (
                        <Certificates certificates={certificates} />
                    )}
                </Stack>
                {dimensionsData && (
                    <ProductData
                        title={`${intl.formatMessage({
                            id: 'pdp.module.specifications.measurements',
                            defaultMessage: 'Product Size (cm/in)'
                        })}`}
                        data={dimensionsData}
                        variant="dimensions"
                    ></ProductData>
                )}
                {netWeightData && (
                    <ProductData
                        title={intl.formatMessage({
                            id: 'pdp.module.specifications.weight',
                            defaultMessage: 'Weight (kg/lbs)'
                        })}
                        data={netWeightData}
                        variant="netWeight"
                    ></ProductData>
                )}
                {suitsAgeData && (
                    <ProductData
                        title={intl.formatMessage({
                            id: 'pdp.module.specifications.suits_age',
                            defaultMessage: 'Suitable for age'
                        })}
                        data={suitsAgeData}
                        variant="suitsAge"
                    ></ProductData>
                )}
                {suitsWeightData && (
                    <ProductData
                        title={intl.formatMessage({
                            id: 'pdp.module.specifications.suits_weight',
                            defaultMessage: 'Suitable for weight (kg/lbs)'
                        })}
                        data={suitsWeightData}
                        variant="suitsWeight"
                    ></ProductData>
                )}
                {extendedWarrantyData && (
                    <ProductData
                        title={intl.formatMessage({
                            id: 'pdp.module.specifications.extended_warranty',
                            defaultMessage: 'Extended warranty'
                        })}
                        data={extendedWarrantyData}
                        variant="extendedWarranty"
                    ></ProductData>
                )}
                {designersData && (
                    <ProductData
                        title={intl.formatMessage({
                            id: 'pdp.module.specifications.design',
                            defaultMessage: 'Design'
                        })}
                        data={designersData}
                        variant="designers"
                    ></ProductData>
                )}
                {characteristicsJSON &&
                    characteristicsJSON.map(({name, value}, index) => (
                        <ProductData
                            key={index}
                            title={name}
                            data={{text: value}}
                            asHTML={true}
                        ></ProductData>
                    ))}
                {product.c_LaunchingDate && (
                    <ProductData
                        title={intl.formatMessage({
                            id: 'pdp.module.specifications.launching_date',
                            defaultMessage: 'Launching Date'
                        })}
                        data={{text: product.c_LaunchingDate}}
                        asHTML={true}
                    ></ProductData>
                )}
                {product.c_ManufacturingCountry && (
                    <ProductData
                        title={intl.formatMessage({
                            id: 'pdp.module.specifications.manufacturing_country',
                            defaultMessage: 'Manufacturing Country'
                        })}
                        data={{text: product.c_ManufacturingCountry}}
                        asHTML={true}
                    ></ProductData>
                )}
                {product.c_CertificationInformation && (
                    <ProductData
                        title={intl.formatMessage({
                            id: 'pdp.module.specifications.certification_information',
                            defaultMessage: 'Certification Information'
                        })}
                        data={{text: product.c_CertificationInformation}}
                        asHTML={true}
                    ></ProductData>
                )}

                {(recommendations?.length > 0 || product.c_productCompatibility) && (
                    <>
                        <Text as="h3" textStyle="body.small" color="text.tertiary">
                            {intl.formatMessage({
                                id: 'pdp.module.specifications.product_compatibility',
                                defaultMessage: 'This product can be used with'
                            })}
                        </Text>

                        {recommendations?.length > 0 && (
                            <Stack css={styles.compatibleProductsContainer}>
                                {recommendations.map((recommendation) => (
                                    <Box
                                        key={recommendation.id}
                                        asChild
                                        css={styles.compatibleProductLink}
                                    >
                                        <Link
                                            to={productUrlBuilder(recommendation, intl.locale)}
                                            variant="primary"
                                        >
                                            {recommendation.name}
                                        </Link>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                        {/* Compatibility text */}
                        {product.c_productCompatibility && (
                            <Text
                                css={styles.compatibilityText}
                                textStyles="body.default"
                                color="text.primary"
                                dangerouslySetInnerHTML={{
                                    __html: product.c_productCompatibility
                                }}
                            />
                        )}
                    </>
                )}
            </Stack>
            {characteristicsImage && (
                <Flex css={styles.characteristicsImageContainer}>
                    <Image
                        css={styles.characteristicsImage}
                        src={characteristicsImage.link}
                        alt={characteristicsImage.alt}
                        data-testid="characteristics-image"
                    ></Image>
                </Flex>
            )}
        </Flex>
    )
}

Specifications.propTypes = {
    product: PropTypes.object
}

export default Specifications
